import { useLocalObservable } from 'mobx-react-lite'
import { Container } from '@/utils/sparrowContainer'
import moment from 'moment'
import useRequest from '../hooks/useLocalRequest'
import { useEffect } from 'react'
import { isImg, isVideo } from '@/utils/bug'
import useDelivery, {
  CommentInfo,
  DeliveryItem,
  OperationInfo
} from '../hooks/useDelivery'

type StatusItem = {
  status: number
  name: string
}
type User = {
  id: string
  name: string
  userNamePinyin: string
}
export type BugItem = {
  /** 后端缺陷 ID（评论 targetId 同源） */
  id: string
  taskId: number
  title: string
  desc: string
  status: number
  onlineDate: string
  userList: User[]
  fromUser: string
  imgs: {
    name: string
    url: string
    isImg: boolean
    isVideo: boolean
  }[]
}
type Store = {
  currentDate: string
  statusList: StatusItem[]
  statusMap: Record<string, string>
  list: BugItem[]
  // 重新获取好点，date 会不同
  deliveryList: DeliveryItem[]
  deliveryMap: Record<string, DeliveryItem>
  tempDeliveryList: DeliveryItem[]
  commentListMap: Record<string, CommentInfo[]>
  currOperationInfos: OperationInfo[]
}

type Props = Store & {
  getList
  handleDateChange
  handleQueryCommentListByTargetIds
  handleCreateComment
  handleQueryOperationListById
  getDeliveryList
  getTempDeliveryList
}

export const storeToData = (item) => {
  const {
    id,
    title: bugName,
    desc: bugRemarks,
    status: bugStatus,
    onlineDate: finishDate,
    fromUser: fromUserId,
    userList,
    imgs,
    taskId
  } = item
  return {
    id,
    taskId: taskId || null,
    bugName,
    bugRemarks,
    bugStatus,
    finishDate,
    fromUserId,
    srcUrl: JSON.stringify(imgs),
    userList: (userList || []).map((user) => {
      return {
        userId: user.id,
        userType: 1
      }
    })
  }
}

export const dataToStore = (item) => {
  const {
    id,
    bugName: title,
    bugRemarks: desc,
    bugStatus: status,
    finishDate: onlineDate,
    fromUserId: fromUser,
    userList,
    srcUrl,
    taskId
  } = item
  let imgs = []
  if (srcUrl) {
    imgs = JSON.parse(srcUrl)
  }
  imgs.forEach((item) => {
    item.isImg = isImg(item.name)
    item.isVideo = isVideo(item.name)
  })
  return {
    id: id != null ? String(id) : '',
    taskId: taskId || null,
    title,
    desc,
    status,
    onlineDate,
    fromUser,
    imgs,
    userList: (userList || [])
      .map((userItem) => {
        const { userId: id, user } = userItem
        if (!id) {
          return null
        }
        const { name, userNamePinyin } = user || {}
        return {
          id,
          name: name ?? '',
          userNamePinyin: userNamePinyin ?? ''
        }
      })
      .filter((entry) => entry != null)
  }
}

function useBugContext(initState: Record<string, string>): Props {
  const store = useLocalObservable<Store>(() => ({
    currentDate: moment().isoWeekday(5).format('YYYY-MM-DD 00:00:00'),
    statusList: [],
    statusMap: {},
    list: [],
    commentListMap: {},
    currOperationInfos: [],
    deliveryList: [],
    deliveryMap: {},
    tempDeliveryList: []
  }))

  const {
    getCommentListByTargetIds,
    createComment,
    getDeliveryList: getDeliveryListReq
  } = useDelivery()

  const listReq = useRequest('/sparrowBug/queryBugList')
  const statusListReq = useRequest('/sparrowBug/queryBugStatusList')
  const recordReq = useRequest('/sparrowBug/getBugOperateLog')
  const getList = async () => {
    const finishDate = store.currentDate
    const res: any = await listReq.run({
      finishDate
    })
    if (res.success) {
      if (finishDate !== store.currentDate) return
      store.list = (res.result || []).map(dataToStore)
      handleQueryCommentListByTargetIds()
    }
  }
  const handleDateChange = (date?: moment.Moment): void => {
    if (date && date.isoWeekday() !== 5) {
      return
    }
    store.currentDate = date ? date.format('YYYY-MM-DD 00:00:00') : null
    getList()
    getDeliveryList()
  }
  const getStatusList = async () => {
    const res = await statusListReq.run('')
    if (res.success && Array.isArray(res.result)) {
      store.statusList = res.result
      const map = {}
      res.result.forEach((item) => {
        map[item.status] = item.name
      })
      store.statusMap = map
    }
  }
  const handleQueryCommentListByTargetIds = () => {
    const ids = store.list.map((item) => item.id).filter(Boolean)
    getCommentListByTargetIds(ids).then((res) => {
      if (res && res.length > 0) {
        const commentListMap: Record<string, CommentInfo[]> = {}
        res.forEach((item) => {
          if (!commentListMap[item.targetId]) {
            commentListMap[item.targetId] = []
          }
          commentListMap[item.targetId].push(item)
        })
        store.commentListMap = { ...commentListMap }
      }
    })
  }
  const handleCreateComment = (payload): Promise<boolean> => {
    return createComment(payload).then((res) => {
      return res
    })
  }
  const handleQueryOperationListById = async (id: string) => {
    store.currOperationInfos = []
    const res: any = await recordReq.run({
      bugId: id
    })
    if (res.success) {
      store.currOperationInfos = [...(res.result || [])]
    }
  }

  const getDeliveryList = () => {
    const finishDate = store.currentDate
    getDeliveryListReq({ finishDate }).then((res) => {
      store.deliveryList = res || []
      const deliveryMap = {}
      store.deliveryList.forEach((item) => {
        deliveryMap[item.id] = item
      })
      store.deliveryMap = deliveryMap
    })
  }
  const getTempDeliveryList = (finishDate) => {
    getDeliveryListReq({ finishDate }).then((res) => {
      store.tempDeliveryList = res || []
    })
  }
  useEffect(() => {
    getStatusList()
  }, [])

  return Object.assign(store, {
    getList,
    handleDateChange,
    handleQueryCommentListByTargetIds,
    handleCreateComment,
    handleQueryOperationListById,
    getDeliveryList,
    getTempDeliveryList
  })
}

export default Container.createContainer<Props, Record<string, string>>(
  useBugContext
)
